import React, { useCallback } from 'react';
import styles from './LeadsSearchFiltersMenu.module.css';
import { Section } from './Section/Section';
import { Subsection } from './Subsection/Subsection';
import { useLeadSearchFilters } from '../use-lead-search-filters';
import {
  LeadsSearchFriendlyName,
  LeadsSearchKeyType,
  LeadsSearchQuery,
} from '@mm/state/interfaces/leads';
import CheckBox from '../../../../../../../common/components/Checkbox';
import _, { isEqual, uniqueId } from 'lodash';
import {
  LocationInputFilter,
  LOCATION_FIELDS,
} from './LocationInputFilter/LocationInputFilter';
import classNames from 'classnames';
import { AgeSlider } from './AgeSlider/AgeSlider';
import {
  convertToUpperSnakeCase,
  convertToCamesCase,
  hasFiltersByKey,
  parseAge,
} from './helpers';
import { Spin } from 'antd';

export const MAX_QUERY_ATTRIBUTES_LENGTH = 25;
export const YES_VALUE = 'Y';
const AGE_KEY = 'AGE';

type Props = {
  filterParams: LeadsSearchQuery;
  setFilterParams: (params: LeadsSearchQuery) => void;
};

export const LeadsSearchFiltersMenu = ({
  filterParams,
  setFilterParams,
}: Props) => {
  const { mapAttributeValuesToCheckboxItems, busy } = useLeadSearchFilters({
    fetchAttributes: true,
    filterParams,
  });

  const shouldDisableFilters =
    filterParams.query.length >= MAX_QUERY_ATTRIBUTES_LENGTH;

  const disableExcludeLocations =
    (filterParams?.exclude &&
      filterParams.exclude.length >= MAX_QUERY_ATTRIBUTES_LENGTH) ||
    !filterParams.query.length;

  const minAge = 18;
  const maxAge = 100;
  const hasAgeFilters = hasFiltersByKey(filterParams.query, AGE_KEY);
  const hasIncludedLocations = hasFiltersByKey(
    filterParams.query,
    LOCATION_FIELDS,
  );
  const hasLocationFilters =
    hasIncludedLocations ||
    hasFiltersByKey(filterParams.exclude, LOCATION_FIELDS);

  const isCheckboxChecked = useCallback(
    (name: LeadsSearchKeyType, value: string) => {
      return !!filterParams.query.filter(arr =>
        arr[1] === YES_VALUE
          ? convertToCamesCase(arr[0]) === name
          : convertToCamesCase(arr[0]) === name && arr[1] === value,
      ).length;
    },
    [filterParams.query],
  );

  const onCheckboxClick = useCallback(
    (name: LeadsSearchKeyType, value: string) => {
      const restParams = {
        exclude: filterParams.exclude,
        offset: 0,
      };

      let updatedQuery;
      if (isCheckboxChecked(name, value)) {
        updatedQuery = {
          query: [
            ...filterParams.query.filter(arr =>
              arr[1] === YES_VALUE
                ? convertToCamesCase(arr[0]) !== name
                : arr[1] !== value,
            ),
          ],
          ...restParams,
        };
      } else {
        updatedQuery = {
          query: [
            ...filterParams.query,
            [convertToUpperSnakeCase(name), value],
          ],
          ...restParams,
        };
      }

      setFilterParams(updatedQuery);
    },
    [filterParams, setFilterParams, isCheckboxChecked],
  );

  const renderCheckbox = useCallback(
    (name: LeadsSearchKeyType, label: string, isList?: boolean) => {
      const isChecked = isCheckboxChecked(name, label);
      return (
        <CheckBox
          key={uniqueId()}
          label={label}
          name={name}
          isChecked={isChecked}
          disabled={isChecked ? false : shouldDisableFilters}
          className={classNames(styles.checkbox)}
          labelClassName={styles.checkboxLabel}
          onClick={() => onCheckboxClick(name, isList ? label : YES_VALUE)}
        />
      );
    },
    [onCheckboxClick, shouldDisableFilters, isCheckboxChecked],
  );

  const renderCheckboxList = useCallback(
    (key: LeadsSearchKeyType) => {
      return mapAttributeValuesToCheckboxItems(key)?.map(checkbox => {
        return renderCheckbox(checkbox.name, checkbox.label, true);
      });
    },
    [mapAttributeValuesToCheckboxItems, renderCheckbox],
  );

  const withSpin = (children: JSX.Element | JSX.Element[]) => {
    return busy ? (
      <Spin size="small" spinning={busy} className={styles.spin}>
        {children}
      </Spin>
    ) : (
      children
    );
  };

  const onAfterAgeChange = (ageRange: [number, number]) => {
    const ageStr = ageRange.join('-');

    const paramsExcludingAge = filterParams.query.filter(
      arr => !arr.some(i => i === AGE_KEY),
    );

    const queryWithReset = isEqual([minAge, maxAge], ageRange)
      ? paramsExcludingAge
      : [...paramsExcludingAge, [AGE_KEY, ageStr]];

    const updatedQuery = {
      query: queryWithReset,
      exclude: filterParams.exclude,
      offset: 0,
    } as LeadsSearchQuery;

    setFilterParams(updatedQuery);
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>Filters</div>

      <div className={styles.filters}>
        <Section name="Professional">
          <Subsection
            name={LeadsSearchFriendlyName.BUS_OWNER}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('busOwner'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.CREDRATE}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('credrate'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.INCOMESTHH}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('incomesthh'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.OCCUPATION}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('occupation'))}
          </Subsection>
          <Subsection name="Professional Hobby" className={styles.subsection}>
            {renderCheckbox('invReal', LeadsSearchFriendlyName.INV_REAL)}
            {renderCheckbox('invStocks', LeadsSearchFriendlyName.INV_STOCKS)}
          </Subsection>
        </Section>

        <Section name="Location & Home" hasActiveFilters={hasLocationFilters}>
          <Subsection
            name={LeadsSearchFriendlyName.HOMEMKTVAL}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('homemktval'))}
          </Subsection>

          <Subsection
            name={LeadsSearchFriendlyName.FUEL}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('fuel'))}
          </Subsection>
          <Subsection
            name="Location"
            className={styles.subsection}
            hasActiveFilters={hasLocationFilters}
          >
            <LocationInputFilter
              disabled={shouldDisableFilters}
              filterParams={filterParams}
              setFilterParams={setFilterParams}
            />
            {hasIncludedLocations ? (
              <div className={styles.locationsBlock}>
                <div className={styles.heading}>Excluding Locations</div>
                <LocationInputFilter
                  disabled={disableExcludeLocations}
                  filterParams={filterParams}
                  setFilterParams={setFilterParams}
                  exclude
                />
              </div>
            ) : (
              <></>
            )}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.HOMEOWNRNT}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('homeownrnt'))}
          </Subsection>
        </Section>

        <Section name="Lifestyle">
          {renderCheckbox('colAntiq', LeadsSearchFriendlyName.COL_ANTIQ)}
          {renderCheckbox('colArts', LeadsSearchFriendlyName.COL_ARTS)}
          {renderCheckbox('beauty', LeadsSearchFriendlyName.BEAUTY)}
          {renderCheckbox('bksmagBks', LeadsSearchFriendlyName.BKSMAG_BKS)}
          {renderCheckbox('catowner', LeadsSearchFriendlyName.CATOWNER)}
          {renderCheckbox('donation', LeadsSearchFriendlyName.DONATION)}
          {renderCheckbox('chGen', LeadsSearchFriendlyName.CH_GEN)}
          {renderCheckbox('colCoin', LeadsSearchFriendlyName.COL_COIN)}
          {renderCheckbox('dogowner', LeadsSearchFriendlyName.DOGOWNER)}
          {renderCheckbox('colSport', LeadsSearchFriendlyName.COL_SPORT)}
        </Section>

        <Section name="Individual" hasActiveFilters={hasAgeFilters}>
          <Subsection
            name={LeadsSearchFriendlyName.AGE}
            className={styles.subsection}
            hasActiveFilters={hasAgeFilters}
          >
            <AgeSlider
              minAge={minAge}
              maxAge={maxAge}
              disabled={shouldDisableFilters}
              onAfterChange={onAfterAgeChange}
              defaultValue={parseAge(filterParams.query, AGE_KEY)}
              resetToDeafults={
                !filterParams.query?.filter(item => item.includes(AGE_KEY))
                  .length
              }
            />
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.EDUCATION}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('education'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.SEX}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('sex'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.PRFL_LGBT_SUPPORT}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('prflLgbtSupport'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.MRTLSTATUS}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('mrtlstatus'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.NUMCHILD}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('numchild'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.NETWORTH}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('networth'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.PARTY_CODE}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('partyCode'))}
          </Subsection>
          <Subsection
            name={LeadsSearchFriendlyName.RELIGION}
            className={styles.subsection}
          >
            {withSpin(renderCheckboxList('religion'))}
          </Subsection>
          <Subsection name="Personal" className={styles.subsection}>
            {renderCheckbox('dieting', LeadsSearchFriendlyName.DIETING)}
            {renderCheckbox(
              'prflGunControl',
              LeadsSearchFriendlyName.PRFL_GUN_CONTROL,
            )}
            {renderCheckbox(
              'prflMarijuanaReform',
              LeadsSearchFriendlyName.PRFL_MARIJUANA_REFORM,
            )}
            {renderCheckbox(
              'prflMilSupport',
              LeadsSearchFriendlyName.PRFL_MIL_SUPPORT,
            )}
            {renderCheckbox('singparent', LeadsSearchFriendlyName.SINGPARENT)}
          </Subsection>
        </Section>

        <Section name="Behaviour">
          <Subsection name="Interests" className={styles.subsection}>
            {renderCheckbox('donEnvir', LeadsSearchFriendlyName.DON_ENVIR)}
            {renderCheckbox('foodWine', LeadsSearchFriendlyName.FOOD_WINE)}
            {renderCheckbox('foodsnat', LeadsSearchFriendlyName.FOODSNAT)}
            {renderCheckbox('gamBoard', LeadsSearchFriendlyName.GAM_BOARD)}
            {renderCheckbox('gamCasino', LeadsSearchFriendlyName.GAM_CASINO)}
            {renderCheckbox('gamComp', LeadsSearchFriendlyName.GAM_COMP)}
            {renderCheckbox('gamVid', LeadsSearchFriendlyName.GAM_VID)}
            {renderCheckbox('gaming', LeadsSearchFriendlyName.GAMING)}
            {renderCheckbox('motorcycle', LeadsSearchFriendlyName.MOTORCYCLE)}
            {renderCheckbox('scispace', LeadsSearchFriendlyName.SCISPACE)}
            {renderCheckbox('scubadiver', LeadsSearchFriendlyName.SCUBADIVER)}
            {renderCheckbox('smoking', LeadsSearchFriendlyName.SMOKING)}
          </Subsection>
          <Subsection name="Sports" className={styles.subsection}>
            {renderCheckbox('specBase', LeadsSearchFriendlyName.SPEC_BASE)}
            {renderCheckbox('boating', LeadsSearchFriendlyName.BOATING)}
            {renderCheckbox('specHock', LeadsSearchFriendlyName.SPEC_HOCK)}
            {renderCheckbox('golf', LeadsSearchFriendlyName.GOLF)}
            {renderCheckbox('nascar', LeadsSearchFriendlyName.NASCAR)}
            {renderCheckbox('snowski', LeadsSearchFriendlyName.SNOWSKI)}
            {renderCheckbox('specSocc', LeadsSearchFriendlyName.SPEC_SOCC)}
            {renderCheckbox('tennis', LeadsSearchFriendlyName.TENNIS)}
          </Subsection>
        </Section>
      </div>
    </div>
  );
};
